import React, { FC, Fragment, useCallback, useState } from 'react';
import { Paper, TableContainer } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { startWorkTemplate } from 'apollo/templates/workFlow';
import { getOrderForRowActionTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import StartWorkDialog from 'components/shared/workflow-actions/start-work-dialog.component';
import { Order, OrderTableType } from 'models/order';
import SchedulePickupFromTruckDialog from 'components/shared/schedule-pickup-from-truck-dialog/schedule-pickup-from-truck-dialog.component';
import OrderItemContextMenu from 'components/orders/shared/order-item-context-menu.component';
import ShareWithExternalDialog from 'components/orders/shared/share-with-external-dialog.component';
import { OrderItemContext, OrderItemEvent, OrderItemEventType } from 'models/order-item-events';
import WorkflowActionsDialog from 'components/shared/workflow-actions/workflow-actions-dialog.component';
import OrderListTable from 'components/orders/lab-order-table/order-list-table.component';
import DentistOrderTable from 'components/orders/dentist-order-table/dentist-order-table.component';
import ExternalOrderTable from 'components/orders/external-order-table/order-list/external-order-table.component';
import InfiniteScroll from 'react-infinite-scroll-component';
import ManageLinkedDentistsDialog from 'components/orders/dentist-order-table/manage-linked-dentists-dialog/manage-linked-dentists-dialog.component';
import { OrderBy } from 'models/search-result';
import PauseResumeConfirmationDialog from 'components/lab-workflow/pause-resume-confirmation-dialog/pause-resume-confirmation-dialog.component';
import ExternalWorkflowOrderTable from './external-order-table/workflow-list/external-workflow-order-table.component';
import InternalOrderIdDialog from 'components/order-details/main-order-view/order-info-section/internal-order-id-dialog.component';
import ChangePanNumberDialog from 'components/order-details/main-order-view/order-info-section/change-pan-number-dialog.component';
import ReceivedByLabDialog from 'components/orders/order-details/received-by-lab-dialog.component';
import SendToClinicDialog from 'components/orders/order-details/send-to-clinic-dialog.component';
import ApprovedOrderDialog from 'components/orders/order-details/approve-order-dialog.component';
import ArchiveOrderDialog from 'components/orders/order-details/archive-order-dialog.component';
import DownloadFilesDialog from 'components/shared/download-files-dialog/download-files-dialog.component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type OrderTableContainerProps = {
    orders: Order[];
    onFetchMore: () => void;
    tableType: OrderTableType;
    hidePatientDetails?: boolean;
    setOrderOnField?: (filed: string) => void;
    orderBy?: OrderBy;
    onUpdateStep?: (orderId: string | undefined) => void;
    onPausedUnpausedStep?: (orderId: string, userId: string, comment: string, reason?: string) => void;
    handleUpdateOrderListForArchived?: (orderId: string) => void;
    batchActionSelected?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean, orderIndex: number) => void;
    labOrderListProfile?: import('models/organization').ListViewProfile;
    isAtLimit?: boolean;
    checkedOrderIds?: Set<string>;
};
const OrderTableContainer: FC<OrderTableContainerProps> = ({
    orders,
    onFetchMore,
    tableType,
    hidePatientDetails,
    setOrderOnField,
    orderBy,
    onUpdateStep,
    onPausedUnpausedStep,
    handleUpdateOrderListForArchived,
    batchActionSelected,
    onHandleCheckedOrders,
    labOrderListProfile,
    isAtLimit,
    checkedOrderIds,
}: OrderTableContainerProps) => {
    const [transportOrderDialogOpen, setTransportOrderDialogOpen] = useState(false);
    const [orderContextMenu, setOrderContextMenu] = useState<OrderItemContext>();
    const [shareWithExternalDialogOpen, setShareWithExternalDialogOpen] = useState(false);
    const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
    const [linkedDentistsDialogOpen, setLinkedDentistsDialogOpen] = useState(false);
    const [currentOrderItemEvent, setCurrentOrderItemEvent] = useState<OrderItemEvent>();
    const [openConfirmPauseResumeDialog, setConfirmDialog] = useState(false);
    const [internalOrderIdDialogOpen, setInternalOrderIdDialogOpen] = useState(false);
    const [changePanNumberDialogOpen, setChangePanNumberDialogOpen] = useState(false);
    const [receivedByLabDialogOpen, setReceivedByLabDialogOpen] = useState(false);
    const [sentByLabDialogOpen, setSentByLabDialogOpen] = useState(false);
    const [approveOrderDialog, setApproveOrderDialogOpen] = useState(false);
    const [archiveOrderDialogOpen, setArchiveOrderDialogOpen] = useState(false);
    const [downloadFilesDialogOpen, setDownloadFilesDialogOpen] = useState(false);
    const [startWorkConfirmOpen, setStartWorkConfirmOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [startWork] = useMutation(startWorkTemplate, {
        onCompleted: () => setStartWorkConfirmOpen(false),
        onError: (error) => showBoundary(error),
    });

    const [fetchOrderForRowAction] = useLazyQuery(getOrderForRowActionTemplate, {
        fetchPolicy: 'network-only',
        onCompleted: ({ order: fetchedOrder }: { order?: Order }) => {
            if (!fetchedOrder) return;
            setOrderContextMenu((current) =>
                current && current.order._id === fetchedOrder._id ? { ...current, order: fetchedOrder } : current,
            );
            setCurrentOrderItemEvent((current) =>
                current && current.order._id === fetchedOrder._id ? { ...current, order: fetchedOrder } : current,
            );
        },
        onError: (error) => showBoundary(error),
    });

    const handleContextMenu = useCallback(
        (orderContext: OrderItemContext) => {
            setOrderContextMenu(orderContextMenu === undefined ? orderContext : undefined);
            if (orderContextMenu === undefined && orderContext.order?._id) {
                fetchOrderForRowAction({ variables: { orderId: orderContext.order._id } });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const handleCloseContextMenu = useCallback(() => {
        setOrderContextMenu(undefined);
    }, []);

    const handleCloseDialogs = useCallback(() => {
        setShareWithExternalDialogOpen(false);
        setWorkflowDialogOpen(false);
        setTransportOrderDialogOpen(false);
        setLinkedDentistsDialogOpen(false);
        setCurrentOrderItemEvent(undefined);
        setConfirmDialog(false);
        setInternalOrderIdDialogOpen(false);
        setChangePanNumberDialogOpen(false);
        setReceivedByLabDialogOpen(false);
        setSentByLabDialogOpen(false);
        setApproveOrderDialogOpen(false);
        setArchiveOrderDialogOpen(false);
        setDownloadFilesDialogOpen(false);
        setStartWorkConfirmOpen(false);
    }, []);

    const handleOrderItemEvent = useCallback((event: OrderItemEvent) => {
        setCurrentOrderItemEvent(event);
        switch (event.eventType) {
            case OrderItemEventType.MANAGE_EXTERNAL_LAB_USERS:
                setShareWithExternalDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_ACCEPTED_BY_LAB:
                setReceivedByLabDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_SENT_BY_LAB:
                setSentByLabDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_APPROVED:
                setApproveOrderDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_ARCHIVE:
                setArchiveOrderDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_INTERNAL_ORDER_ID:
                setInternalOrderIdDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_PAN_NUMBER:
                setChangePanNumberDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_WORKFLOW:
                setWorkflowDialogOpen(true);
                break;
            case OrderItemEventType.TOGGLE_PAUSE_STEP:
                setConfirmDialog(true);
                break;
            case OrderItemEventType.TOGGLE_TRUCK_ICON:
                setTransportOrderDialogOpen(true);
                return;
            case OrderItemEventType.MANAGE_LINKED_DENTISTS:
                setLinkedDentistsDialogOpen(true);
                break;
            case OrderItemEventType.MANAGE_DOWNLOAD_FILES:
                setDownloadFilesDialogOpen(true);
                break;
            case OrderItemEventType.START_WORK:
                setStartWorkConfirmOpen(true);
                break;
        }
        handleCloseContextMenu();
    }, []);

    const handleClose = () => {
        setConfirmDialog(false);
    };

    const handleOk = async (userId: string, comment: string, reason?: string) => {
        if (onPausedUnpausedStep) {
            onPausedUnpausedStep(currentOrderItemEvent?.order?._id as string, userId, comment, reason);
        }
        setConfirmDialog(false);
    };

    const handleOrderUpdateForArchived = (order: Order) => {
        if (handleUpdateOrderListForArchived) {
            handleUpdateOrderListForArchived(order._id);
        }
        if (onUpdateStep) {
            onUpdateStep(order._id);
        }
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, orders?.length || 0);

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <InfiniteScroll
                    style={{ padding: 0 }}
                    dataLength={orders ? orders.length : 0}
                    next={onFetchMore}
                    throttleTimeInMs={75}
                    hasMore={true}
                    loader={undefined}
                >
                    {tableType === OrderTableType.DENTIST ? (
                        <DentistOrderTable
                            orders={orders}
                            onOrderItemEvent={handleOrderItemEvent}
                            onContextMenu={handleContextMenu}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={onHandleCheckedOrders}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    ) : undefined}

                    {tableType === OrderTableType.LAB ||
                    tableType === OrderTableType.SUPPLIER ||
                    tableType === OrderTableType.LAB_WORKFLOW ? (
                        <OrderListTable
                            tableType={tableType}
                            orders={orders}
                            orderBy={orderBy}
                            setOrderOnField={setOrderOnField}
                            onOrderItemEvent={handleOrderItemEvent}
                            onContextMenu={handleContextMenu}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={onHandleCheckedOrders}
                            profile={labOrderListProfile}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    ) : undefined}

                    {tableType === OrderTableType.EXTERNAL_TECHNICIAN ||
                    tableType === OrderTableType.SUPPLIER_EXTERNAL_TECHNICIAN ? (
                        <ExternalOrderTable
                            orderBy={orderBy}
                            setOrderOnField={setOrderOnField}
                            orders={orders}
                            hidePatientDetails={hidePatientDetails}
                            onContextMenu={handleContextMenu}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={onHandleCheckedOrders}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    ) : undefined}

                    {tableType === OrderTableType.EXTERNAL_TECHNICIAN_WORKFLOW ||
                    tableType === OrderTableType.SUPPLIER_EXTERNAL_TECHNICIAN_WORKFLOW ? (
                        <ExternalWorkflowOrderTable
                            orderBy={orderBy}
                            setOrderOnField={setOrderOnField}
                            orders={orders}
                            hidePatientDetails={hidePatientDetails}
                            onContextMenu={handleContextMenu}
                            batchActionSelected={batchActionSelected}
                            onHandleCheckedOrders={onHandleCheckedOrders}
                            isAtLimit={isAtLimit}
                            checkedOrderIds={checkedOrderIds}
                        />
                    ) : undefined}
                </InfiniteScroll>
            </TableContainer>

            {transportOrderDialogOpen ? (
                <SchedulePickupFromTruckDialog
                    open={transportOrderDialogOpen}
                    onClose={handleCloseDialogs}
                    orderId={currentOrderItemEvent?.order._id}
                />
            ) : undefined}

            {shareWithExternalDialogOpen ? (
                <ShareWithExternalDialog
                    orderId={currentOrderItemEvent?.order._id}
                    open={shareWithExternalDialogOpen}
                    onClose={handleCloseDialogs}
                />
            ) : undefined}

            {linkedDentistsDialogOpen ? (
                <ManageLinkedDentistsDialog
                    orderId={currentOrderItemEvent?.order._id}
                    open={linkedDentistsDialogOpen}
                    onClose={handleCloseDialogs}
                />
            ) : undefined}

            {workflowDialogOpen ? (
                <WorkflowActionsDialog
                    open={workflowDialogOpen}
                    onClose={handleCloseDialogs}
                    orderId={currentOrderItemEvent?.order._id}
                    onStepChanged={() => onUpdateStep?.(currentOrderItemEvent?.order._id)}
                />
            ) : undefined}

            {openConfirmPauseResumeDialog ? (
                <PauseResumeConfirmationDialog
                    isPaused={currentOrderItemEvent?.order.workFlow?.currentStep?.isPaused ?? false}
                    initialUserId={currentOrderItemEvent?.order.workFlow?.currentStep?.userId ?? undefined}
                    open={openConfirmPauseResumeDialog}
                    handleClose={handleClose}
                    handleOk={handleOk}
                />
            ) : undefined}

            <OrderItemContextMenu
                onOrderItemEvent={handleOrderItemEvent}
                orderContext={orderContextMenu}
                onCloseContextMenu={handleCloseContextMenu}
            />

            {internalOrderIdDialogOpen ? (
                <InternalOrderIdDialog
                    open={internalOrderIdDialogOpen}
                    onClose={handleCloseDialogs}
                    order={currentOrderItemEvent?.order}
                />
            ) : undefined}

            {changePanNumberDialogOpen ? (
                <ChangePanNumberDialog
                    open={changePanNumberDialogOpen}
                    onClose={handleCloseDialogs}
                    order={currentOrderItemEvent?.order}
                />
            ) : undefined}

            {receivedByLabDialogOpen ? (
                <ReceivedByLabDialog
                    open={receivedByLabDialogOpen}
                    onClose={handleCloseDialogs}
                    orderId={currentOrderItemEvent?.order._id}
                />
            ) : undefined}

            {sentByLabDialogOpen ? (
                <SendToClinicDialog
                    open={sentByLabDialogOpen}
                    onClose={handleCloseDialogs}
                    orderId={currentOrderItemEvent?.order._id}
                />
            ) : undefined}

            {approveOrderDialog ? (
                <ApprovedOrderDialog
                    open={approveOrderDialog}
                    onClose={handleCloseDialogs}
                    orderId={currentOrderItemEvent?.order._id}
                />
            ) : undefined}

            {archiveOrderDialogOpen ? (
                <ArchiveOrderDialog
                    onUpdate={handleOrderUpdateForArchived}
                    open={archiveOrderDialogOpen}
                    onClose={handleCloseDialogs}
                    orderId={currentOrderItemEvent?.order._id as string}
                />
            ) : undefined}
            {downloadFilesDialogOpen ? (
                <DownloadFilesDialog
                    orderId={currentOrderItemEvent?.order._id as string}
                    open={downloadFilesDialogOpen}
                    onClose={handleCloseDialogs}
                />
            ) : undefined}

            {startWorkConfirmOpen ? (
                <StartWorkDialog
                    open={startWorkConfirmOpen}
                    stepName={currentOrderItemEvent?.order.workFlow?.currentStep?.name}
                    onConfirm={(userId) => {
                        startWork({ variables: { orderId: currentOrderItemEvent?.order._id, userId } });
                    }}
                    onClose={() => setStartWorkConfirmOpen(false)}
                />
            ) : undefined}
        </Fragment>
    );
};

export default OrderTableContainer;
